import { useEffect } from "react";
import type { TimelineWindow } from "../../api/types";
import { heardThisSession, listeningSinceMs } from "../../state/listened";
// Aliased: `playheadMs` is the name the frame's own value carries below, and the rule that
// produces it reads better as a verb there than as a shadowed import.
import { audioMs, playheadMs as playhead, usePlayer } from "../../state/player";
import { type DrawInput, draw } from "../draw";
import { ghostTrack } from "../ghost";
import { liveDelayFromMs, selectionAtMs, timeToX, worthFollowing } from "../geometry";
import { timelineHeight } from "../theme";
import { type TimelineCtx, type TimelineRefs, setViewWidth } from "./state";

/**
 * How stale the drawn clock may get before the canvas is redrawn for its own sake.
 *
 * Nothing else marks the canvas dirty when the clock is the only thing that moved, and two
 * things on screen track it: the now marker, and the right edge of anything still running.
 * Driven from the render loop rather than from a timer, so a hidden tab does no work at all --
 * requestAnimationFrame stops when the tab is hidden and setInterval does not.
 */
const REPAINT_MS = 500;

/** Everything the loop reads that does not live in a ref. */
export interface PaintDeps extends TimelineCtx {
  zone: string;
  chatLane: boolean;
  compact: boolean;
}

type PlayerState = ReturnType<typeof usePlayer.getState>;

/** Carried across frames rather than closed over, so a frame can be a plain function. */
interface LoopState {
  frame: number;
  drawnNowMs: number;
  /**
   * The playhead the selection was last derived from, so a stationary one is not re-derived
   * every frame.
   */
  derivedFromMs: number;
}

/**
 * Match the backing store to the element, and re-derive the request windows from the new width.
 *
 * A plain function and not a closure inside the effect: it writes the view through `r`, which
 * React's compiler will not allow a hook to do directly.
 */
function sizeCanvas(
  r: TimelineRefs,
  container: HTMLDivElement,
  canvas: HTMLCanvasElement,
  chatLane: boolean,
  compact: boolean,
  syncWindow: () => void,
): void {
  const width = container.clientWidth;
  const height = timelineHeight(chatLane, compact);
  // Backing store in device pixels, coordinate system in CSS pixels: without this the
  // canvas is soft on every display with a scale factor, which is most of them.
  const ratio = window.devicePixelRatio || 1;
  canvas.width = Math.round(width * ratio);
  canvas.height = Math.round(height * ratio);
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  const ctx = canvas.getContext("2d");
  ctx?.setTransform(ratio, 0, 0, ratio, 0, 0);

  setViewWidth(r, width);
  syncWindow();
}

/** Size it now and on every resize of the container, for as long as the effect is mounted. */
function observeSize(
  r: TimelineRefs,
  chatLane: boolean,
  compact: boolean,
  syncWindow: () => void,
): (() => void) | undefined {
  const container = r.container.current;
  const canvas = r.canvas.current;
  if (!container || !canvas) return;
  const resize = () => sizeCanvas(r, container, canvas, chatLane, compact, syncWindow);

  resize();
  const observer = new ResizeObserver(resize);
  observer.observe(container);
  return () => observer.disconnect();
}

/** Size the backing store to the element, and re-derive the request windows when it changes. */
export function useCanvasSize(
  r: TimelineRefs,
  chatLane: boolean,
  compact: boolean,
  syncWindow: () => void,
): void {
  // chatLane belongs here: the backing store and the device-pixel transform are only set
  // inside resize(), so a lane appearing without a re-run leaves a canvas whose height no
  // longer matches what the bands draw into.
  useEffect(
    () => observeSize(r, chatLane, compact, syncWindow),
    [r, chatLane, compact, syncWindow],
  );
}

// Not while an edge is being dragged: following would slide the timeline out from
// under the pointer and the edge would land somewhere nobody aimed at.
//
// Nor while a pan is aiming. The gesture owns the centre from the first move until its
// landing resolves, and `follow` turning back on underneath it -- a previous gesture's
// landing, resolving late -- makes two authorities on the same number: this pins the
// centre back on the playhead every frame while the bar goes on drawing the aim. What
// that looks like is the bug it was found as: a timeline that will not pan, and a
// playhead sliding the opposite way to the mouse, because the aim moves against the
// drag while the centre it is measured from stands still.
function followPlayhead(p: PaintDeps, player: PlayerState, followMs: number): void {
  const r = p.refs;
  if (
    player.follow &&
    r.draft.current === null &&
    r.aim.current === null &&
    worthFollowing(r.view.current, followMs)
  ) {
    // Follow mode pins the playhead to the centre and slides the content beneath it,
    // which is the whole conceit of this timeline.
    r.view.current = { ...r.view.current, centerMs: followMs };
    r.dirty.current = true;
    p.syncWindow();
  }
}

// What the bar is over is what is selected. One rule for both gestures and for neither:
// a click moves the bar and the selection follows it there, a drag carries it along, and
// a track that simply ends hands the border to the one that starts -- which nothing did
// before, because every path that selected anything was a pointer event and playback is
// not one.
//
// `barMs`, which is the instant the bar is drawn at, and not the store's playhead, which
// is only sometimes the same number. Two places they part, and the border belongs with
// the bar in both. At the live edge the bar is held back to the delay wall while the
// playhead is the bare clock, so the playhead names a block up to a delay later than the
// one the bar is standing on. And an unresolved landing is the whole of the aim's
// purpose: from the moment a drag is released until `landOn` writes the position, the
// store still holds the instant the gesture dragged away from, and the audio goes on
// reporting it four times a second. Reading that put the border back on the previous
// track for the length of the landing's two lookups and then forward again.
//
// Only when the bar has actually moved -- a stationary one has nothing to re-derive --
// and only inside the window that is loaded. The second because panning away fetches a
// window that does not contain the bar, where "nothing here covers it" is ignorance
// rather than an answer, and clearing a good selection on it is the wrong reading.
function deriveSelection(
  p: PaintDeps,
  s: LoopState,
  current: TimelineWindow | null,
  barMs: number,
  nowMs: number,
  player: PlayerState,
): void {
  if (barMs === s.derivedFromMs || !current || p.refs.panning.current) return;
  s.derivedFromMs = barMs;
  if (barMs >= Date.parse(current.start) && barMs < Date.parse(current.end)) {
    const next = selectionAtMs(current.tracks, current.shows, barMs, nowMs, player.selectedId);
    // Before the dirty flag is cleared: `select` marks the canvas dirty through the store
    // subscription, and this frame is already drawing the new border.
    if (next !== player.selectedId) player.select(next);
  }
}

/** Everything the frame is drawn from, gathered in the order the canvas paints it. */
function frameInput(
  p: PaintDeps,
  current: TimelineWindow | null,
  barMs: number,
  nowMs: number,
  delayFromMs: number | null,
  player: PlayerState,
): DrawInput {
  const r = p.refs;
  const draft = r.draft.current;
  const listeningSince = listeningSinceMs();
  return {
    view: r.view.current,
    t: p.t,
    chatLane: p.chatLane,
    compact: p.compact,
    zone: p.zone,
    shows: current?.shows ?? [],
    tracks: current?.tracks ?? [],
    gaps: current?.gaps ?? [],
    archived: current?.archived ?? [],
    listened: current?.listened ?? null,
    // Read here rather than held in state: this is the animation loop, it already runs on
    // every position report, and the array only changes when one lands.
    listenedNow: heardThisSession(),
    // Against the audio's own position and never `playheadMs`: the grey is a claim about
    // what has been heard, and at the live edge the playhead is the clock, which runs on
    // while the transport sits paused -- the rule crept across a whole show nobody was
    // listening to. Extrapolated, like the bar is, so while audio does play the two edges
    // travel together instead of the grey trailing the cursor until the next report.
    listeningMs: listeningSince === null ? null : [listeningSince, audioMs(player, nowMs)],
    chat: current?.chat ?? [],
    chatGaps: current?.chat_gaps ?? [],
    waveform: r.waveform.current,
    // The aim of a pan, or the clamped follow target, so the now marker appears the moment
    // the two separate: in live mode they now always do, and that marker is what bounds
    // the delay band on the right.
    playheadMs: barMs,
    // Re-read rather than taken off the snapshot above: the derivation a few lines up may
    // have just moved it, and drawing the old border would leave the change a frame late.
    selectedId: usePlayer.getState().selectedId,
    hoverId: r.hover.current,
    nowMs,
    // Off the same bounds the pan wall is taken from (see `clampView`), so the stripes
    // start exactly where the view stops. `r.bounds` already marks the canvas dirty when
    // it changes, so the loop needs no new dependency.
    archiveStartMs: r.bounds.current?.archive_start
      ? Date.parse(r.bounds.current.archive_start)
      : null,
    // Null off live, because `wallDelayMs` is zero there and there is nothing to mark: the
    // band and the wall are the same number by construction, so the view cannot stop
    // anywhere this does not explain.
    liveDelayFromMs: delayFromMs,
    liveTrack: ghostTrack(r.nowPlaying.current, current, nowMs),
    editable: p.editable,
    draft: draft
      ? {
          id: draft.id,
          start: new Date(draft.startMs).toISOString(),
          end: new Date(draft.endMs).toISOString(),
        }
      : null,
    snapMs: draft?.snap ? (draft.edge === "start" ? draft.startMs : draft.endMs) : null,
  };
}

function renderFrame(p: PaintDeps, s: LoopState): void {
  const r = p.refs;
  const canvas = r.canvas.current;
  const ctx = canvas?.getContext("2d");
  if (!ctx || r.view.current.width === 0) return;

  const player = usePlayer.getState();
  const nowMs = Date.now();
  // Where the playhead is, by the one rule the store owns: at the live edge the clock, and
  // live audio's own position while it is actually reporting one. This is what keeps the
  // timeline moving on a page where nobody has pressed play, and what stops a live stream
  // that never started from freezing the ruler at the instant it was selected. Read here
  // rather than subscribed to: `usePlayheadMs` would re-render this component every second
  // to report something only the canvas is looking at.
  const playheadMs = playhead(player, nowMs);

  // Where the archive stops, computed once: it holds the follow target back below, it is
  // the wall a landing may not cross, and it is the band the canvas draws further down.
  // Two readings of the clock would put the wall and the wash a frame apart, which at the
  // top of the zoom range is a visible pixel.
  const delayFromMs = liveDelayFromMs(p.latestMs(), nowMs, p.walls.wallDelayMs());

  // Where following may actually put the centre bar. With nothing playing the store's
  // playhead is the clock, and the clock is inside the delay band -- parking the bar there
  // would draw it over the one stretch this canvas has just finished calling unreachable.
  // Clamped once, before `worthFollowing` as well as after: comparing against a target the
  // assignment will not reach leaves the two permanently a delay apart, and the loop
  // re-syncing the fetch window every frame for a move it never makes.
  //
  // Only against a live wall, never against a stale one. `latest` arrives with the window
  // and a window loaded from last Tuesday reports last Tuesday, so a wall built from it
  // would pin the view there -- and a pinned view never moves, never re-syncs the window
  // and never learns better. Going live would hang on the spot. `liveDelayFromMs` is null
  // in exactly that case, which is what lets the next frame fetch its way out.
  const followMs = delayFromMs === null ? playheadMs : Math.min(playheadMs, delayFromMs);

  // What the bar denotes this frame. The aim of a pan in progress outranks the store: the
  // gesture is dragging the content under a fixed playhead, and the position the store
  // still holds is the one being dragged away from. Only the drawing is affected -- the
  // audio goes on playing where it is, and the landing is what makes the aim true.
  const barMs = r.aim.current ?? followMs;

  followPlayhead(p, player, followMs);

  // The clock only changes the picture while it is on screen: it is where the now marker sits
  // and how far an on-air block reaches. Off to the right of the viewport that block already
  // spans the whole width and none of its visible geometry moves.
  if (
    timeToX(r.view.current, nowMs) <= r.view.current.width &&
    nowMs - s.drawnNowMs >= REPAINT_MS
  ) {
    r.dirty.current = true;
  }

  if (!r.dirty.current) return;

  const current = r.data.current;
  deriveSelection(p, s, current, barMs, nowMs, player);

  r.dirty.current = false;
  s.drawnNowMs = nowMs;
  r.scene.current = draw(ctx, frameInput(p, current, barMs, nowMs, delayFromMs, player));
}

/** The animation loop. One frame per rAF tick, and a repaint only when something is stale. */
export function useTimelinePaint(p: PaintDeps): void {
  useEffect(() => {
    const s: LoopState = { frame: 0, drawnNowMs: 0, derivedFromMs: Number.NaN };
    const tick = () => {
      s.frame = requestAnimationFrame(tick);
      renderFrame(p, s);
    };
    s.frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(s.frame);
  }, [p]);
}
